import React, { useEffect, useState, useContext } from "react";
import { Button, Typography, Menu, MenuItem, IconButton } from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { AuthContext } from "./AuthContext";
import { useLoginState } from "./LoginState";
import { getAuth, signOut } from "firebase/auth";
import { SignIn } from "./SignIn";
import { SignUp } from "./SignUp";
import { Forgot } from "./Forgot";
import AuthModal  from "./AuthModal";
import ProfileModal from "./ProfileModal";
import { PersonIconButton } from "./styles";
import { MyProfilePic } from "./MyProfilePic";
import { Reset } from "./Reset";
import { UserProfileRole } from "ts-interfaces";
import { WorkLogin } from "./WorkLogin";
import { VerificationScreen } from "./VerificationScreen";
import VerificationFailed from "./VerificationFailed";

type LoginModalType = "signin" | "signup" | "forgot" | "reset" | "work_login" | "work_login_success" | "verification_screen" | "verification_failed" | null;

export const LoginModal: React.FC = () => {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const showLogin = useLoginState((state) => state.showLogin) as LoginModalType;
  
  const handleClose = (): void => {
    setShowLogin(null);
  };
  
  const signupClose = (): void => {
    // when signing up prevent closing.
    setShowLogin(null)
    return;
  };

  if (showLogin === "signin") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <SignIn />
      </AuthModal>
    );
  } else if (showLogin === "signup") {
    return (
      <AuthModal open={true} onClose={signupClose}>
        <>
          {" "}
          <SignUp />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "forgot") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <Forgot />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "reset") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <Reset />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "work_login") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <WorkLogin />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "work_login_success") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <SignUp activeStep={1} />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "verification_screen") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <VerificationScreen />{" "}
        </>
      </AuthModal>
    );
  } else if (showLogin === "verification_failed") {
    return (
      <AuthModal open={true} onClose={handleClose}>
        <>
          {" "}
          <VerificationFailed />{" "}
        </>
      </AuthModal>
    );
  } else return null;
};

interface Profile {
  uid?: string;
  userRole?: UserProfileRole;
  files?: {
    pic?: string;
  };
}

interface UserData {
  user: {
    uid: string;
    email: string | null;
  };
}

export const AuthButton = ({setShowSubscription}) => {
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  
  const { currentUser } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const profile = useLoginState((state) => state.profile) as any;
  const setProfile = useLoginState((state) => state.setProfile);
  const getUserProfile = useLoginState((state) => state.getUserProfile);
  const [profileModalShown, setProfileModalShown] = useState<boolean>(false);
  const setOrCreateProfile = useLoginState((state) => state.setOrCreateProfile);
  const [attemptedProfile, setAttemptedProfile] = useState<boolean>(false);  
  const [showCreatorLinks, setShowCreatorLinks] = useState<boolean>((window as any).MATTO_PROJECT != 'teams-admin' );

  useEffect(() => { 
    if (!currentUser?.uid) { 
      setProfile({}); 
      return; 
    }
    loadProfile(); 
  }, [currentUser?.uid]);

  useEffect(() => {
    if (attemptedProfile === false) return;
    setTimeout(() => {
      if (!currentUser?.uid) { 
        setProfile({}); 
        return; 
      }
      const loginState:any = useLoginState.getState();
      if (loginState.profile?.uid) return;
      const data: UserData = { 
        user: { 
          uid: currentUser.uid,
          email: (currentUser as any).email,
        } 
      };
      console.log('setOrCreateProfile called from Auth for user:', data.user.email);
      setOrCreateProfile(data);
    }, 1500);
  }, [attemptedProfile, currentUser]);

  const loadProfile = (force: boolean = false): void => {
    if (!currentUser?.uid) { 
      setProfile({}); 
      return; 
    }
    if ((useLoginState.getState() as any).profile?.uid && force !== true) return;
    const data: UserData = { 
      user: { 
        uid: currentUser.uid,
        email: (currentUser as any).email,
      } 
    };
    getUserProfile(data).then(() => { setAttemptedProfile(true); });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = (): void => {    
    setAnchorEl(null);
    setOpen(false);
  };

  const goToProfile = (): void => {
    handleClose();
    setProfileModalShown(true);
  };

  const goToProjects = (): void => { 
    handleClose(); 
    window.location.href = "/projects"; 
  };

  const cancelProSubscription = (): void => { 
    handleClose(); 
    setShowSubscription('cancel'); 
  };

  const goToAccounts = (): void => { 
    handleClose(); 
    setShowSubscription('accounts'); 
  };

  const handleLogout = (): void => {
    sessionStorage.clear();
    signOut(getAuth()).finally(() => {
      setShowLogin(null);
      handleClose();
    });
  };

  const handleProfileModalSuccess = (): void => { 
    loadProfile(true); 
  };

  return (
    <>
      {currentUser ? (
        <div>
          <IconButton
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleClick}
            color="inherit"
            size="large"
          >
          <MyProfilePic img={profile?.files?.pic} />
          </IconButton>

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem onClick={goToProfile}>Your Profile</MenuItem>

            {showCreatorLinks && 
              <>
              <MenuItem onClick={goToProjects}>Your Projects</MenuItem>
              <MenuItem onClick={goToAccounts}>Accounts & Billing</MenuItem>       
              </>
            }
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>

          {profileModalShown && (
          <ProfileModal
              open={profileModalShown}
              initialProfile={profile}
              onClose={() => setProfileModalShown(false)}
              onSuccess={handleProfileModalSuccess}
            />
          )}
        </div>
      ) : (
        <>
          <PersonIconButton edge="start" onClick={() => setShowLogin("signup")}>
            <PersonIcon  sx={{color:'black'}} />
          </PersonIconButton>
        </>
      )}
      <LoginModal />
    </>
  );
};

//export default AuthButton;
